var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useRef } from "react";
import { TextField, Grid, Typography, Autocomplete, Box, createFilterOptions, Alert, } from "@mui/material";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomModal from "../modal/CustomModal";
import ObjectStatusApi from "../../../infrastructure/api/pages/object-status-api";
import { useTranslation } from "react-i18next";
const validationSchema = (translate) => yup.object().shape({
    id: yup.string().nullable(),
    name: yup.string().required(translate("NameRequired")),
    parentId: yup.string().nullable(),
    isActive: yup.boolean(),
});
const ObjectStatusFormSubmit = ({ open, onClose, onSuccess, initialFormData, data, title, allStatuses, }) => {
    var _a, _b;
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { t: translate } = useTranslation();
    const hiddenButtonRef = useRef(null);
    const { reset, handleSubmit, control, register, formState: { errors }, } = useForm({
        resolver: yupResolver(validationSchema(translate)),
    });
    const onCloseModal = () => {
        reset({
            id: initialFormData.id,
            name: initialFormData.name,
            parentId: initialFormData.parentId,
            isActive: initialFormData.isActive,
        });
        setErrorMessage("");
        onClose();
    };
    const onSubmit = (formData) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        setSaving(true);
        setErrorMessage("");
        // Frontend validation: Check for duplicate Level 1 names
        const isLevel1 = !formData.parentId; // Level 1 has no parent
        if (isLevel1) {
            const trimmedName = ((_a = formData.name) === null || _a === void 0 ? void 0 : _a.trim()) || "";
            const isDuplicate = allStatuses.some(status => status.id !== formData.id && // Exclude current status when editing
                status.level === 1 &&
                !status.parentId &&
                status.name.trim().toLowerCase() === trimmedName.toLowerCase());
            if (isDuplicate) {
                const errorMsg = translate("DuplicateLevel1StatusName") ||
                    `A Level 1 status with the name '${trimmedName}' already exists. Please use a different name.`;
                setErrorMessage(errorMsg);
                toast.error(errorMsg);
                setSaving(false);
                return;
            }
        }
        try {
            const response = formData.id
                ? yield ObjectStatusApi.update(formData)
                : yield ObjectStatusApi.create(Object.assign(Object.assign({}, formData), { id: null }));
            if (response.status === 200) {
                toast.success(translate(formData.id ? "UpdatedSuccessfully" : "SavedSuccessfully"));
                onCloseModal();
                onSuccess();
            }
        }
        catch (error) {
            // Extract error message from response
            const backendError = ((_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.error) ||
                ((_d = error === null || error === void 0 ? void 0 : error.response) === null || _d === void 0 ? void 0 : _d.data) ||
                (error === null || error === void 0 ? void 0 : error.message);
            // Display user-friendly error messages
            if (backendError === null || backendError === void 0 ? void 0 : backendError.includes("already exists")) {
                const errorMsg = translate("DuplicateLevel1StatusName") || backendError;
                setErrorMessage(errorMsg);
                toast.error(errorMsg);
            }
            else if ((backendError === null || backendError === void 0 ? void 0 : backendError.includes("exceed maximum depth")) ||
                (backendError === null || backendError === void 0 ? void 0 : backendError.includes("children with depth"))) {
                const errorMsg = translate("CannotChangeParentDepthExceeded") ||
                    "Cannot change parent: This would exceed the maximum hierarchy depth of 3 levels.";
                setErrorMessage(errorMsg);
                toast.error(errorMsg);
            }
            else if (backendError === null || backendError === void 0 ? void 0 : backendError.includes("circular reference")) {
                const errorMsg = translate("CannotCreateCircularReference") ||
                    "Cannot set this parent: This would create a circular reference.";
                setErrorMessage(errorMsg);
                toast.error(errorMsg);
            }
            else if ((backendError === null || backendError === void 0 ? void 0 : backendError.includes("system status")) || (backendError === null || backendError === void 0 ? void 0 : backendError.includes("system-defined"))) {
                toast.error(translate("CannotModifySystemStatus") || "Cannot modify system-defined status");
            }
            else {
                setErrorMessage(backendError || translate("InternalServerError"));
                toast.error(translate("InternalServerError"));
            }
        }
        finally {
            setSaving(false);
        }
    });
    useEffect(() => {
        if (data) {
            reset({
                id: data.id || null,
                name: data.name,
                parentId: data.parentId || null,
                isActive: data.isActive !== undefined ? data.isActive : true,
            });
            setErrorMessage("");
        }
    }, [data, reset]);
    // Helper: Calculate maximum depth of children for a status node
    const getMaxChildDepth = (node) => {
        if (!node.children || node.children.length === 0) {
            return 0;
        }
        let maxDepth = 0;
        node.children.forEach((child) => {
            const childDepth = getMaxChildDepth(child);
            maxDepth = Math.max(maxDepth, childDepth);
        });
        return maxDepth + 1;
    };
    // Helper: Check if a node is a descendant of another node (for circular reference prevention)
    const isDescendantOf = (candidateDescendantId, potentialAncestorId, nodes) => {
        if (candidateDescendantId === potentialAncestorId) {
            return true;
        }
        for (const node of nodes) {
            if (node.id === potentialAncestorId) {
                if (node.children && node.children.length > 0) {
                    for (const child of node.children) {
                        if (child.id === candidateDescendantId ||
                            isDescendantOf(candidateDescendantId, child.id, [child])) {
                            return true;
                        }
                    }
                }
            }
            else if (node.children && node.children.length > 0) {
                if (isDescendantOf(candidateDescendantId, potentialAncestorId, node.children)) {
                    return true;
                }
            }
        }
        return false;
    };
    // Check if the current status being edited has children
    const currentStatusHasChildren = Boolean(data.id && data.children && data.children.length > 0);
    const maxChildDepth = currentStatusHasChildren ? getMaxChildDepth(data) : 0;
    // Flatten tree for parent selection dropdown
    // Only show levels 1 and 2 as parent options (max 3 levels allowed)
    // Exclude system-defined statuses from being selectable as parents
    const flattenForDropdown = (nodes, level = 1) => {
        let result = [];
        nodes.forEach((node) => {
            // Basic exclusions:
            // 1. Don't allow selecting itself as parent
            // 2. Only show up to level 2 (parent can be level 1 or 2 only)
            // 3. Exclude system statuses
            // 4. Don't allow selecting descendants (circular reference)
            const isItself = node.id === data.id;
            const isSystemStatus = node.isSystemStatus;
            const isDescendant = data.id ? isDescendantOf(node.id, data.id, allStatuses) : false;
            // Additional validation for statuses with children:
            // If the status being edited has children, ensure selecting this parent won't exceed depth 3
            let wouldExceedDepth = false;
            if (currentStatusHasChildren) {
                // New level would be: parent.level + 1
                // Total depth would be: newLevel + maxChildDepth
                const newLevel = node.level + 1;
                const totalDepth = newLevel + maxChildDepth;
                wouldExceedDepth = totalDepth > 3;
            }
            if (!isItself && node.level < 3 && !isSystemStatus && !isDescendant && !wouldExceedDepth) {
                result.push({
                    id: node.id,
                    name: node.name,
                    level: node.level,
                    isSystemStatus: node.isSystemStatus,
                });
                if (node.children && node.children.length > 0) {
                    result = result.concat(flattenForDropdown(node.children, node.level + 1));
                }
            }
        });
        return result;
    };
    const parentOptions = flattenForDropdown(allStatuses);
    // Check if parent dropdown should be disabled
    // Disable if status has children and no valid parent options exist
    const noValidParentOptions = currentStatusHasChildren && parentOptions.length === 0;
    return (_jsx(CustomModal, { open: open, onClose: onCloseModal, size: "sm", saving: saving, handleSaveButtonClick: () => { var _a; return (_a = hiddenButtonRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, heading: title, saveButtonText: translate("Save"), disabled: false, children: _jsx(Grid, { container: true, spacing: 0, children: _jsx(Grid, { size: {
                    lg: 8,
                    xs: 12
                }, sx: {
                    mx: "auto"
                }, children: _jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [_jsx("input", Object.assign({ type: "hidden" }, register("id"))), _jsx(Controller, { name: "name", control: control, defaultValue: data.name, render: ({ field }) => {
                                var _a;
                                return (_jsx(TextField, Object.assign({ label: translate("Name"), variant: "outlined", fullWidth: true, margin: "normal" }, field, { error: !!errors.name, helperText: (_a = errors.name) === null || _a === void 0 ? void 0 : _a.message })));
                            } }), _jsx(Controller, { name: "parentId", control: control, defaultValue: data.parentId || null, render: ({ field }) => (_jsx(Autocomplete
                            // --- Enable search by name ---
                            , { 
                                // --- Enable search by name ---
                                filterOptions: createFilterOptions({
                                    stringify: (option) => option.name,
                                }), 
                                // --- Controlled value ---
                                value: parentOptions.find((option) => option.id === field.value) ||
                                    null, onChange: (_, newValue) => field.onChange(newValue ? newValue.id : null), options: parentOptions, getOptionLabel: (option) => (option === null || option === void 0 ? void 0 : option.name) || "", isOptionEqualToValue: (option, value) => option.id === value.id, disabled: noValidParentOptions, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("ParentStatus"), variant: "outlined", margin: "normal", fullWidth: true }))), 
                                // --- Custom option layout with clear hierarchy ---
                                renderOption: (props, option) => {
                                    const isParent = option.level === 1; // top level = parent
                                    return (_jsx(Box, Object.assign({ component: "li" }, props, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            py: 1,
                                            pl: (theme) => `${theme.spacing(option.level * 2)} !important`, // force indentation
                                            fontWeight: isParent ? 600 : 400,
                                            color: (theme) => isParent
                                                ? theme.palette.text.primary
                                                : theme.palette.text.secondary,
                                            borderLeft: !isParent
                                                ? "1px solid rgba(0,0,0,0.1)"
                                                : "none",
                                            minHeight: 40,
                                            "&:hover": {
                                                backgroundColor: (theme) => theme.palette.action.hover,
                                            },
                                        }, children: _jsx(Typography, { variant: "body2", noWrap: true, sx: {
                                                flex: 1,
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                            }, children: option.name }) })));
                                }, slotProps: {
                                    listbox: {
                                        sx: {
                                            maxHeight: 260,
                                            overflowY: "auto",
                                            px: 0.5,
                                            py: 0.5,
                                            "& li": {
                                                borderRadius: "8px",
                                            },
                                        },
                                    }
                                } })) }), noValidParentOptions && (_jsx(Alert, { severity: "error", sx: {
                                mt: 2,
                                mb: 2,
                                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                '& .MuiAlert-icon': {
                                    fontSize: '1.2rem'
                                }
                            }, children: _jsxs(Typography, { variant: "body2", sx: { fontSize: '0.875rem' }, children: [_jsxs("strong", { children: [translate("CannotChangeParent") || "Cannot Change Parent", ":"] }), " ", translate("This status has") || "This status has", " ", (_a = data.children) === null || _a === void 0 ? void 0 : _a.length, " ", translate("child status(es)") || "child status(es)", " ", translate("with maximum depth of") || "with maximum depth of", " ", maxChildDepth, " ", translate("level(s)") || "level(s)", ". ", translate("No valid parent options available as any change would exceed the 3-level maximum.") || "No valid parent options available as any change would exceed the 3-level maximum."] }) })), currentStatusHasChildren && !noValidParentOptions && data.id && (_jsx(Alert, { severity: "warning", sx: {
                                mt: 2,
                                mb: 2,
                                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                '& .MuiAlert-icon': {
                                    fontSize: '1.2rem'
                                }
                            }, children: _jsxs(Typography, { variant: "body2", sx: { fontSize: '0.875rem' }, children: [_jsxs("strong", { children: [translate("Warning"), ":"] }), " ", translate("This status has") || "This status has", " ", (_b = data.children) === null || _b === void 0 ? void 0 : _b.length, " ", translate("child status(es)") || "child status(es)", " ", translate("with maximum depth of") || "with maximum depth of", " ", maxChildDepth, " ", translate("level(s)") || "level(s)", ". ", translate("Parent options are limited to prevent exceeding 3-level maximum.") || "Parent options are limited to prevent exceeding 3-level maximum."] }) })), !noValidParentOptions && (_jsx(Alert, { severity: "info", sx: {
                                mt: currentStatusHasChildren ? 1 : 3,
                                mb: 2,
                                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                                '& .MuiAlert-icon': {
                                    fontSize: '1.2rem'
                                }
                            }, children: _jsxs(Typography, { variant: "body2", sx: { fontSize: '0.875rem' }, children: [_jsxs("strong", { children: [translate("Tip"), ":"] }), " ", translate("Leave Parent Status dropdown blank to create a Level 1 status. Select a Level 1 or Level 2 parent to create a sub-status. Maximum 3 levels are allowed.")] }) })), _jsx("button", { ref: hiddenButtonRef, type: "submit", hidden: true })] }) }) }) }));
};
export default ObjectStatusFormSubmit;
